import { useEffect, useState, useCallback, useRef } from 'react'
import { Search, Package, Layers, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type { Product, ProductVariant, AdminERP } from '../../types'
import { formatCurrency } from '../../utils/helpers'
import ProductDetailModal from '../../components/ProductDetailModal'

const PAGE_SIZE = 50

export default function AdminProducts() {
  const { t } = useTranslation()
  const user = api.getUser()
  const isSuperAdmin = user?.role === 'super_admin'

  // ERP selection (super_admin only)
  const [erps, setErps] = useState<AdminERP[]>([])
  const [selectedErpId, setSelectedErpId] = useState<number | null>(null)

  // Products
  const [products, setProducts] = useState<Product[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const pageRef = useRef(1)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Variants state
  const [variantsByProduct, setVariantsByProduct] = useState<Record<string, ProductVariant[]>>({})
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({})
  const fetchingVariants = useRef<Set<string>>(new Set())

  // Detail modal
  const [detailProduct, setDetailProduct] = useState<Product | null>(null)

  // Broken images tracking
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set())

  // Load ERP list for super_admin
  useEffect(() => {
    if (isSuperAdmin) {
      api
        .getAdminERPs()
        .then((list) => {
          setErps(list)
          if (list.length > 0) setSelectedErpId(list[0].id)
        })
        .catch(() => {})
    }
  }, [isSuperAdmin])

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(t)
  }, [searchTerm])

  const erpId = isSuperAdmin ? selectedErpId : user?.erp_id || null

  const loadProducts = useCallback(
    async (reset = false) => {
      if (!erpId) return
      if (reset) {
        setLoading(true)
        pageRef.current = 1
      } else {
        setLoadingMore(true)
      }
      try {
        const result = await api.getAdminProducts({
          erp_id: erpId,
          search: debouncedSearch || undefined,
          page: pageRef.current,
          limit: PAGE_SIZE,
        })
        const items = result.data ?? []
        if (reset) {
          setProducts(items)
        } else {
          setProducts((prev) => [...prev, ...items])
        }
        setTotalCount(result.pagination?.total ?? items.length)
        setHasMore(pageRef.current < (result.pagination?.pages ?? 1))
        pageRef.current += 1
      } catch {
        if (reset) setProducts([])
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [erpId, debouncedSearch]
  )

  // Reload when ERP or search changes
  useEffect(() => {
    setVariantsByProduct({})
    setSelectedVariants({})
    fetchingVariants.current.clear()
    loadProducts(true)
  }, [erpId, debouncedSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadProducts(false)
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loadingMore, loadProducts])

  // Fetch variants when products load
  useEffect(() => {
    for (const product of products) {
      if (!product.has_variants) continue
      if (fetchingVariants.current.has(product.id) || variantsByProduct[product.id]) continue
      fetchingVariants.current.add(product.id)
      api
        .getAdminProductVariants(product.id, erpId ?? undefined)
        .then((variants) => {
          setVariantsByProduct((prev) => ({ ...prev, [product.id]: variants }))
          if (variants.length > 0) {
            setSelectedVariants((prev) =>
              prev[product.id] ? prev : { ...prev, [product.id]: variants[0].id }
            )
          }
        })
        .catch(() => {})
    }
  }, [products]) // eslint-disable-line react-hooks/exhaustive-deps

  // Build image URL for a product (admin endpoint)
  const getImageUrl = (productId: string): string => {
    const token = localStorage.getItem('auth_token')
    const apiBase = import.meta.env.VITE_API_URL || '/api'
    const erpParam = erpId ? `&erp_id=${erpId}` : ''
    return `${apiBase}/admin/products/${productId}/image?token=${token}${erpParam}`
  }

  const renderProductImage = (product: Product) => {
    if (product.image && !brokenImages.has(product.id)) {
      return (
        <img
          src={getImageUrl(product.id)}
          alt={product.label}
          className="w-full h-full object-contain"
          onError={() => setBrokenImages((prev) => new Set(prev).add(product.id))}
        />
      )
    }
    return <Package className="w-16 h-16 text-gray-300" />
  }

  // Modal variant state for the selected product
  const modalVariants = detailProduct ? (variantsByProduct[detailProduct.id] ?? null) : null
  const modalSelectedVariantId = detailProduct ? selectedVariants[detailProduct.id] : undefined

  if (!erpId && isSuperAdmin && erps.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('adminProducts.title', 'Catalogue produits')}
          </h1>
          {totalCount > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              {t('adminProducts.count', { count: totalCount })}
            </p>
          )}
        </div>

        {/* ERP selector (super_admin) */}
        {isSuperAdmin && (
          <select
            value={selectedErpId ?? ''}
            onChange={(e) => setSelectedErpId(Number(e.target.value) || null)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {erps.map((erp) => (
              <option key={erp.id} value={erp.id}>
                {erp.erp_name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder={t('common.search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Products grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Package className="w-12 h-12 mx-auto mb-3" />
          <p className="text-sm">{t('adminProducts.noProducts', 'Aucun produit trouvé.')}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map((product) => {
              const variants = variantsByProduct[product.id]
              const selVariantId = selectedVariants[product.id]
              const selVariant = variants?.find((v) => v.id === selVariantId)
              const displayPrice = selVariant?.price ?? product.price
              const displayPriceTtc = selVariant?.price_ttc ?? product.price_ttc

              return (
                <div
                  key={product.id}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col hover:shadow-md transition-shadow"
                >
                  {/* Image */}
                  <button
                    onClick={() => setDetailProduct(product)}
                    className="relative h-40 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center overflow-hidden group"
                  >
                    {renderProductImage(product)}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                        {t('catalog.viewDetails', 'Voir le détail')}
                      </span>
                    </div>
                  </button>

                  {/* Info */}
                  <div className="p-4 flex flex-col gap-2 flex-1">
                    <div>
                      <p className="text-xs text-gray-400 font-mono">{product.ref}</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2">
                        {product.label}
                      </p>
                    </div>

                    {/* Variant selector */}
                    {product.has_variants && (
                      <div>
                        {variants == null ? (
                          <div className="h-8 bg-gray-100 rounded animate-pulse" />
                        ) : variants.length > 0 ? (
                          <select
                            value={selVariantId ?? ''}
                            onChange={(e) =>
                              setSelectedVariants((prev) => ({
                                ...prev,
                                [product.id]: e.target.value,
                              }))
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-primary-500 outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            {variants.map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                            <Layers className="w-3 h-3" />
                            {t('catalog.noVariants')}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Price */}
                    <div className="mt-auto pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-400">{t('catalog.priceExclTax')}</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {formatCurrency(displayPrice)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">{t('catalog.priceInclTax')}</p>
                        <p className="text-sm font-bold text-primary-600">
                          {formatCurrency(displayPriceTtc)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-8 flex items-center justify-center">
            {loadingMore && (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            )}
          </div>
        </>
      )}

      {/* Product detail modal */}
      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          imageNode={renderProductImage(detailProduct)}
          onClose={() => setDetailProduct(null)}
          quantity={1}
          onQuantityChange={() => {}}
          onAddToCart={() => {}}
          isAdded={false}
          variants={modalVariants}
          selectedVariantId={modalSelectedVariantId}
          onVariantChange={(id) =>
            setSelectedVariants((prev) => ({ ...prev, [detailProduct.id]: id }))
          }
          variantDescription={
            modalVariants?.find((v) => v.id === modalSelectedVariantId)?.description
          }
        />
      )}
    </div>
  )
}
